import NavHeaderTransformer from "~/transformers/NavHeaderTransformer";
import { resolveActiveTabId } from "~/transformers/NavTabsTransformer";

/**
 * Translate function injected from the component (`this.$t`). Keeps the
 * transformer pure and unit-testable.
 */
type Translate = (key: string) => string;

export type HeaderContext = {
  isFederated: boolean;
  // "/agencies/{agencyId}" | "/federated/{instanceId}/{agencyId}"
  basePath: string;
  agencyName: string;
  // Only present for federated instances.
  instanceName?: string;
  // selectedAgency?.agentsConfig?.telemetry
  hasTelemetry: boolean;
};

type SelectedAgency = {
  agencyId?: string;
  name?: string;
  agentsConfig?: { telemetry?: boolean };
};

type SelectedInstance = {
  instanceId?: string;
  agencyId?: string;
  // Name of the opened organism (federated agency / tab name).
  name?: string;
  // Name of the parent (mother) federated instance.
  instanceName?: string;
};

export type HeaderContextInput = {
  isFederated: boolean;
  selectedAgency?: SelectedAgency | null;
  selectedInstance?: SelectedInstance | null;
};

export type TabOption = {
  id: string;
  label: string;
  icon: string;
};

type Section = {
  // Relative path segment appended to basePath; "" is the dashboard root.
  segment: string;
  labelKey: string;
  icon: string;
};

/**
 * Sections rendered as tabs inside a regular agency, in display order.
 * `telemetry` is conditional on the agency config.
 */
const AGENCY_SECTIONS: Section[] = [
  { segment: "", labelKey: "agencyHeader.tabs.commandCenter", icon: "tachometter" },
  { segment: "computers", labelKey: "computers.label", icon: "computer" },
  { segment: "vaccines", labelKey: "vaccines.label", icon: "syringe" },
  { segment: "alerts", labelKey: "alerts.label", icon: "alert" },
  { segment: "telemetry", labelKey: "telemetry.label", icon: "hashtag-light" },
];

/** Federated instances only expose the command-center dashboard. */
const FEDERATED_SECTIONS: Section[] = [AGENCY_SECTIONS[0]];

const joinPath = (basePath: string, segment: string): string =>
  segment ? `${basePath}/${segment}` : basePath;

/**
 * Maps the raw store selection (agency or federated instance) into the
 * presentation-ready `HeaderContext`. Keeps path-building and the
 * agency-vs-instance field mapping out of the component.
 */
export const toHeaderContext = (input: HeaderContextInput): HeaderContext => {
  if (input.isFederated) {
    const { instanceId, agencyId, name, instanceName } = input.selectedInstance ?? {};
    return {
      isFederated: true,
      basePath: `/federated/${instanceId}/${agencyId}`,
      // `instanceName` is the parent (mother) instance; `name` is the opened
      // organism within it.
      instanceName,
      agencyName: name ?? "",
      hasTelemetry: false,
    };
  }
  const agency = input.selectedAgency ?? {};
  return {
    isFederated: false,
    basePath: `/agencies/${agency.agencyId}`,
    agencyName: agency.name ?? "",
    hasTelemetry: Boolean(agency.agentsConfig?.telemetry),
  };
};

/**
 * Breadcrumb pieces for the global header.
 *   agency   → [Gestión, Organismos, agencyName]
 *   federado → [Organismos federados, instanceName, agencyName]
 * Empty pieces are dropped so callers can rely on a non-empty array.
 */
export const toBreadcrumb = (ctx: HeaderContext, t: Translate): string[] => {
  if (ctx.isFederated) {
    return [t("menu.federatedAgencies"), ctx.instanceName ?? "", ctx.agencyName].filter(Boolean);
  }
  return [t("menu.management"), t("agencies.label"), ctx.agencyName].filter(Boolean);
};

/**
 * Section tabs for `s2-tab-buttons`. `id` is the absolute route path so the
 * component can navigate (`@input`) and resolve the active tab by route.
 */
export const toTabOptions = (ctx: HeaderContext, t: Translate): TabOption[] => {
  const sections = ctx.isFederated ? FEDERATED_SECTIONS : AGENCY_SECTIONS;
  return sections
    .filter((section) => section.segment !== "telemetry" || ctx.hasTelemetry)
    .map((section) => ({
      id: joinPath(ctx.basePath, section.segment),
      label: t(section.labelKey),
      icon: NavHeaderTransformer.toIconClass(section.icon),
    }));
};

/**
 * Active tab id = the longest tab path that prefixes the current route.
 * Reuses the longest-prefix matcher shared with the agency tab strip.
 */
export const toActiveTabId = (tabIds: string[], currentPath: string): string | null =>
  resolveActiveTabId(tabIds, currentPath);

/**
 * Returns true for routes that belong to agency or federated-agency sections.
 * Used by layouts/main.vue to decide when to show the agency loading overlay.
 */
export const isAgencyRoute = (routeName: string | null | undefined): boolean => {
  if (!routeName) return false;
  return routeName.startsWith("agencies-agency") || routeName.startsWith("federated-");
};
